import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Space, Tag, Modal, Card, Row, Col, Statistic,
  Empty, Typography, Divider, message as antMessage,
} from 'antd';
import {
  ReloadOutlined, FilePdfOutlined, DollarOutlined,
  ExclamationCircleOutlined, UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchAllDebtors, markInvoicePaid } from '../../../services/chargesApi';

const { Text, Title } = Typography;

const brl = (v) => `R$ ${(parseFloat(v) || 0).toFixed(2).replace('.', ',')}`;

const DefaultersTab = () => {
  const [debtors, setDebtors] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllDebtors();
      setDebtors(data.debtors || []);
      setGrandTotal(data.grand_total || 0);
    } catch {
      antMessage.error('Erro ao carregar inadimplentes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkPaid = (invoice, playerName) => {
    Modal.confirm({
      title: 'Baixar fatura manualmente',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p><strong>Jogador:</strong> {playerName}</p>
          <p><strong>Cobrança:</strong> {invoice.setting_description}</p>
          <p><strong>Valor:</strong> {brl(invoice.total_amount)}</p>
          <p>Confirma que este pagamento foi recebido fora do sistema?</p>
        </div>
      ),
      okText: 'Sim, baixar',
      cancelText: 'Voltar',
      onOk: async () => {
        setDownloadingId(invoice.invoice_id);
        try {
          await markInvoicePaid(invoice.invoice_id);
          antMessage.success('Fatura baixada');
          load();
        } catch {
          antMessage.error('Erro ao baixar fatura');
        } finally {
          setDownloadingId(null);
        }
      },
    });
  };

  const generatePdf = async () => {
    if (debtors.length === 0) {
      antMessage.info('Sem inadimplentes pra exportar');
      return;
    }
    try {
      const jsPDF = (await import('jspdf')).default;
      await import('jspdf-autotable');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

      // Header
      doc.setFontSize(16);
      doc.text('Extrato de Inadimplentes', 40, 40);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Gerado em ${dayjs().format('DD/MM/YYYY HH:mm')}`, 40, 58);
      doc.text(`${debtors.length} jogador(es) • Total: ${brl(grandTotal)}`, 40, 72);
      doc.setTextColor(0);

      let cursorY = 96;
      debtors.forEach((d, idx) => {
        // Cabeçalho do jogador
        if (cursorY > 720) { doc.addPage(); cursorY = 40; }
        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(`${idx + 1}. ${d.player_name}`, 40, cursorY);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.setTextColor(80);
        const meta = [
          d.player_phone ? `Tel: ${d.player_phone}` : null,
          `${d.pending_invoices_count} fatura(s)`,
          `Total: ${brl(d.total_due)}`,
        ].filter(Boolean).join(' • ');
        doc.text(meta, 40, cursorY + 14);
        doc.setTextColor(0);
        cursorY += 24;

        const rows = d.invoices.map((inv) => [
          dayjs(inv.created_at).format('DD/MM/YY HH:mm'),
          inv.setting_description,
          inv.charge_description || '-',
          brl(inv.total_amount),
        ]);

        doc.autoTable({
          startY: cursorY,
          head: [['Emissão', 'Cobrança', 'Descrição', 'Valor']],
          body: rows,
          theme: 'striped',
          styles: { fontSize: 8, cellPadding: 4 },
          headStyles: { fillColor: [255, 77, 79], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 80 },
            3: { cellWidth: 70, halign: 'right' },
          },
          margin: { left: 40, right: 40 },
        });
        cursorY = doc.lastAutoTable.finalY + 18;
      });

      // Rodapé com total geral
      if (cursorY > 730) { doc.addPage(); cursorY = 40; }
      doc.setDrawColor(200);
      doc.line(40, cursorY, 555, cursorY);
      cursorY += 16;
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Total geral: ${brl(grandTotal)}`, 40, cursorY);

      doc.save(`inadimplentes-${dayjs().format('YYYY-MM-DD')}.pdf`);
    } catch (err) {
      console.error(err);
      antMessage.error('Erro ao gerar PDF');
    }
  };

  const expandedRowRender = (record) => (
    <div style={{ padding: '8px 0' }}>
      <Table
        rowKey="invoice_id"
        dataSource={record.invoices}
        pagination={false}
        size="small"
        columns={[
          {
            title: 'Emissão',
            dataIndex: 'created_at',
            width: 140,
            render: (v) => dayjs(v).format('DD/MM/YYYY HH:mm'),
          },
          {
            title: 'Cobrança',
            dataIndex: 'setting_description',
            render: (v, r) => (
              <Space direction="vertical" size={0}>
                <Text strong>{v}</Text>
                {r.charge_description && (
                  <Text type="secondary" style={{ fontSize: 12 }}>{r.charge_description}</Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'payment_status',
            width: 110,
            render: () => <Tag color="red">Em aberto</Tag>,
          },
          {
            title: 'Valor',
            dataIndex: 'total_amount',
            width: 110,
            align: 'right',
            render: (v) => <Text strong>{brl(v)}</Text>,
          },
          {
            title: 'Ação',
            key: 'action',
            width: 130,
            render: (_, inv) => (
              <Button
                size="small"
                type="primary"
                icon={<DollarOutlined />}
                loading={downloadingId === inv.invoice_id}
                onClick={() => handleMarkPaid(inv, record.player_name)}
              >
                Baixar
              </Button>
            ),
          },
        ]}
        summary={() => (
          <Table.Summary>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <Text strong>Total do jogador ({record.pending_invoices_count} fatura(s))</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <Text strong style={{ color: '#cf1322' }}>{brl(record.total_due)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Jogadores inadimplentes"
              value={debtors.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total em aberto"
              value={grandTotal}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ display: 'flex', alignItems: 'center' }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                Atualizar
              </Button>
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={generatePdf}
                disabled={debtors.length === 0}
              >
                Gerar PDF
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
        Considera inadimplente quem tem fatura em aberto emitida há mais de 2 dias.
      </div>

      {debtors.length === 0 && !loading ? (
        <Card>
          <Empty description="Nenhum inadimplente 🎉" />
        </Card>
      ) : (
        <Table
          rowKey="player_id"
          loading={loading}
          dataSource={debtors}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          expandable={{ expandedRowRender, expandRowByClick: true }}
          columns={[
            {
              title: 'Jogador',
              dataIndex: 'player_name',
              render: (v) => <Text strong>{v}</Text>,
            },
            { title: 'Telefone', dataIndex: 'player_phone', width: 140 },
            {
              title: 'Faturas em aberto',
              dataIndex: 'pending_invoices_count',
              width: 160,
              align: 'center',
              render: (v) => <Tag color="red">{v}</Tag>,
            },
            {
              title: 'Total devido',
              dataIndex: 'total_due',
              width: 160,
              align: 'right',
              render: (v) => (
                <Text strong style={{ color: '#cf1322' }}>{brl(v)}</Text>
              ),
              sorter: (a, b) => a.total_due - b.total_due,
              defaultSortOrder: 'descend',
            },
          ]}
          summary={() => debtors.length > 0 && (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total geral ({debtors.length} jogador(es))</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong style={{ color: '#cf1322', fontSize: 15 }}>
                    {brl(grandTotal)}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )}
    </div>
  );
};

export default DefaultersTab;
